/**
 * Changing when a question is asked.
 *
 * The same endpoint every other field option goes through, and a different
 * policy, because this is the one thing the endpoint does not simply store.
 * A condition is a row: one sent with an id the server does not know is
 * *created*, and the id it gets back is the only handle on it afterwards. So
 * the answer is written back, and the change is not applied before it arrives
 * -- a client that showed its own guess would send that guess again on the next
 * edit and make a second condition out of the first.
 *
 * **The whole list is sent every time.** `after_field_options_update` deletes
 * every condition of the field options it is given that the payload does not
 * name, so an edit to one is a rewrite of all of them -- including the ones
 * inside groups, which this build cannot edit and must not destroy.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { ViewFieldOptions, ViewFilterGroup } from '@/api/types'
import { readFieldOptions, withFieldOptions } from '../table/field-options'
import type { Condition } from '../table/condition-row'
import type { FormFieldOptions } from './options'

export interface ConditionChanges {
  conditions: Condition[]
  condition_groups?: readonly ViewFilterGroup[]
}

/** What a form question's options hold about its conditions. */
export interface StoredConditions {
  conditions?: readonly Condition[]
  condition_groups?: readonly ViewFilterGroup[]
}

export function useUpdateFormConditions(
  viewId: number,
): (fieldId: number, changes: ConditionChanges) => void {
  const queryClient = useQueryClient()
  const key = qk.fieldOptions(viewId)

  const update = useMutation({
    mutationFn: ({
      fieldId,
      changes,
    }: {
      fieldId: number
      changes: ConditionChanges
    }): Promise<ViewFieldOptions> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/field-options/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the seven view types.
          body: { field_options: { [fieldId]: changes } } as never,
        }),
      ),

    onSuccess: (answer, { fieldId }) => {
      // Only the two the server derived. The answer carries every option of
      // every field, and writing all of it back would put a label somebody is
      // still typing back to what it was when the request went out.
      const stored = readFieldOptions<StoredConditions>(answer)[String(fieldId)]
      queryClient.setQueryData<ViewFieldOptions>(key, (held) =>
        withFieldOptions<FormFieldOptions & StoredConditions>(held, {
          [fieldId]: {
            conditions: stored?.conditions ?? [],
            condition_groups: stored?.condition_groups ?? [],
          },
        }),
      )
    },
  })

  return (fieldId, changes) => {
    update.mutate({ fieldId, changes })
  }
}
